import { Chip, Grid, Icon, IconButton, Link, Menu, MenuItem } from '@material-ui/core'
import Badge from '@material-ui/core/Badge'
import {
  Assignment,
  AssignmentInd,
  AssignmentLate,
  AssignmentReturned,
  AssignmentTurnedIn,
  Block,
  Edit,
  FindInPage,
  Help,
  Save,
  Forum,
} from '@material-ui/icons/'
import { createStyles, withStyles } from '@material-ui/styles'
import React, { useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { sortedByBoolean, sortedByDateLex } from '../../core'
import { DateInput, daysHoursMinutesFromNow, formatDate, formatLocalDate } from '../../core/date-time'
import { documentResolver } from '../../core/document-resolver'
import { Flag, summariesByState, Visit, WorksheetState, WorksheetStateLabel, WorksheetSummary } from '../../core/domain'
import { personName, withTooltip } from '../../core/format'
import { green, mediumGray, orange, red, yellow } from '../../core/theme'
import { transitionDialogueStore } from '../../core/transitions'
import { worksheetQueue } from '../../core/visit-queue'

export type FacilityWorksheetIconProps = Partial<WorksheetSummary> & {
  iconProps?: any
  flags?: Flag[]
  visit?: Visit
  hideFlags?: boolean
}

function statusColor(label: WorksheetStateLabel): string {
  switch (label) {
    case 'Audited':
    case 'Finalized':
    case 'Coded':
      return green
    case 'Unassigned':
      return mediumGray
    case 'Checked out for Review':
    case 'Checked Out':
      return orange
    case 'Coding Review':
    case 'Returned to Coder':
    case 'Returned to Code Reviewer':
    case 'Skipped Audit':
    case 'Force to Code Review':
    case 'Audit':
    case 'Stashed':
    case 'Returned to Provider':  
    case 'Sent to Provider':
      return yellow
    case 'No Code':  
    case 'QA':
      return red
    default:
      return mediumGray
  }
}

export function StateIcon(props: FacilityWorksheetIconProps) {
  const { label } = props.stateInfo || {}
  const iconProps = { ...{ htmlColor: mediumGray }, ...props.iconProps }

  let htmlColor = statusColor(label)

  switch (label) {
    case 'Unassigned':
      return <Assignment {...iconProps} htmlColor={htmlColor} />
    case 'Audited':
    case 'Finalized':
    case 'Coded':
      return <AssignmentTurnedIn {...iconProps} htmlColor={htmlColor} />
    case 'Checked out for Review':
    case 'Checked Out':
      return <AssignmentInd {...iconProps} htmlColor={htmlColor} />
    case 'Skipped Audit':
      return <AssignmentReturned {...iconProps} htmlColor={htmlColor} />
    case 'Stashed':
      return <Save {...iconProps} htmlColor={htmlColor} />
    case 'Returned to Coder':
    case 'Force to Code Review':
    case 'Audit':
    case 'Coding Review':
      return <FindInPage htmlColor={htmlColor} />
    case 'No Code':
      return <Block htmlColor={htmlColor} />
    case 'QA':
      return <AssignmentLate {...iconProps} htmlColor={htmlColor} />
    case 'Returned to Code Reviewer':
      return <Forum {...iconProps} htmlColor={htmlColor} />
  }
  return <Assignment {...iconProps} htmlColor={htmlColor} />
}

const badgeSize = 14
const SingleLetterBadge: any = withStyles(() =>
  createStyles({
    badge: {
      right: 0,
      top: 0,
      fontSize: '8px',
      padding: '1px 0px 0px 0px',
      marginRight: -3,
      color: 'white',
      textAlign: 'right',
      width: badgeSize,
      minWidth: badgeSize,
      maxWidth: badgeSize,
      height: badgeSize,
      border: '1px solid white',
      backgroundColor: (props) => (props && props.backgroundcolor) || mediumGray,
    },
  }),
)(Badge)

export function FacilityWorksheetIcon(props: FacilityWorksheetIconProps) {
  const { visit, worksheetType, hideFlags } = props
  const ref = useRef()
  const [anchorEl, setAnchorEl] = React.useState(null)

  const editableSummaries = sortedByDateLex(
    visit.wsSummaries.filter((a) => a.editable && a.worksheetType === worksheetType && a.stateInfo?.label?.length),
    (a) => a.serviceDate as string,
    true,
  )

  if (!editableSummaries.length) {
    return <></>
  }

  const onClick = () => {
    transitionDialogueStore.setState({
      open: true,
      summary: props as WorksheetSummary,
      transitionComplete: (s: WorksheetSummary) => {
        props.state = s.stateInfo.name as WorksheetState
        props.stateInfo = s.stateInfo
        worksheetQueue.getState().refreshRefs()
      },
    })
  }

  const onMouseOver = () => {
    setAnchorEl(ref.current)
  }

  const onClose = () => {
    setAnchorEl(null)
  }

  const onSelect = (a: WorksheetSummary) => {
    visit.worksheetId = a.worksheetId
    documentResolver.openWindow(visit)
    onClose()
  }

  const type = editableSummaries[0].worksheetType.substring(0, 1)
  const selectedId = editableSummaries[0].worksheetId

  const states = Object.values(summariesByState(editableSummaries)).reverse()
  const summaries: WorksheetSummary[] = sortedByDateLex(visit.wsSummaries, (a) => a.serviceDate as string, true)
  sortedByBoolean(visit.wsSummaries, (a) => a.editable, true)
  return (
    <>
      <Grid ref={ref} container direction="row" spacing={0} style={{ width: '100%', flexWrap: 'nowrap' }}>
        {states.map((a) => {
          const color = statusColor(a[0].stateInfo?.label)
          const flags = a[0].flags
          return (
            <>
              <Grid
                item
                container
                direction="row"
                style={{ flexWrap: 'nowrap', padding: 0, marginLeft: -6 }}
                onMouseOver={onMouseOver}
              >
                <Grid item alignItems="center" style={{ overflow: 'hidden' }}>
                  <div
                    id="foo"
                    data-name="state-number"
                    style={{
                      color,
                      fontFamily: 'Arial',
                      fontSize: 15,
                      fontWeight: 'bold',
                      overflow: 'hidden',
                    }}
                  >
                    {a.length}
                  </div>
                </Grid>
                <Grid item>
                  <SingleLetterBadge badgeContent={type} backgroundcolor={color} onClick={onClick}>
                    <StateIcon {...a[0]} />
                  </SingleLetterBadge>
                </Grid>
                {hideFlags
                  ? undefined
                  : flags?.length && (
                      <Grid item style={{ paddingLeft: 16, paddingTop: 6, paddingRight: 14 }}>
                        <WorksheetFlags flags={flags} />
                      </Grid>
                    )}
              </Grid>
            </>
          )
        })}
      </Grid>
      <SummarySelectionMenu
        selectedId={selectedId}
        anchorEl={anchorEl}
        onSelect={onSelect}
        onClose={onClose}
        summaries={summaries}
      />
    </>
  )
}

export function AgeInQueue(input: DateInput) {
  const label = daysHoursMinutesFromNow(input)
  return withTooltip(formatDate(input), <Chip label={label} />)
}

interface AccountLinkProps {
  route: string
  icon: string
  locationCode?: string
  account?: string
  target?: string
  ref?: any
  onClick?: (e?: any) => void
}

export function AccountLink(props: AccountLinkProps) {
  const { route, locationCode, account, target, icon, onClick, ref } = props
  const href = locationCode ? `${route}?locationCode=${locationCode}&account=${account}` : route
  return (
    <Link onClick={onClick} component={RouterLink} target={target || null} to={href} style={{ textDecoration: 'none' }}>
      <IconButton color="primary">
        <Icon>{icon}</Icon>
      </IconButton>
    </Link>
  )
}

type SummarySelectionMenuProps = {
  selectedId: string
  anchorEl: any
  summaries: WorksheetSummary[]
  onSelect: (a: WorksheetSummary) => void
  onClose: () => void
}

function WorksheetFlags({ flags }) {
  return (
    <Grid item container direction="row" alignItems="center" justify="center" spacing={1} style={{}}>
      {flags &&
        flags.map((v: Flag) =>
          withTooltip(
            v.description,
            <Grid item>
              <div
                style={{
                  color: 'white',
                  backgroundColor: v.resolved ? '#44aa44' : '#888',
                  textAlign: 'center',
                  borderRadius: 2,
                  paddingTop: 1,
                  margin: 1,
                  fontSize: 8,
                  width: 18,
                }}
              >
                {v.code}
              </div>
            </Grid>,
          ),
        )}
    </Grid>
  )
}

export function SummarySelectionMenu(props: SummarySelectionMenuProps) {
  const { summaries, anchorEl, onSelect, onClose, selectedId } = props

  const onSelected = (a: WorksheetSummary) => {
    onSelect(a)
  }

  return (
    <Menu
      variant="menu"
      anchorEl={anchorEl}
      anchorPosition={{ top: 0, left: 0 }}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      keepMounted
      onClose={onClose}
      open={anchorEl}
      MenuListProps={{ onMouseLeave: onClose }}
    >
      {summaries.map((summary) => {
        const { worksheetId, editable, workflowOwners, state, flags, owningProvider } = summary
        const stateInfo = summary.stateInfo
        const owner = workflowOwners?.length && workflowOwners[workflowOwners.length - 1]
        return (
          <MenuItem
            dense={true}
            key={worksheetId}
            selected={worksheetId === selectedId}
            onClick={() => onSelected(summary)}
          >
            {stateInfo ? (
              <>
                <StateIcon {...summary} />
                &nbsp;&nbsp;
              </>
            ) : (
              <>
                <Help htmlColor="#ccc" />
                &nbsp;&nbsp;
              </>
            )}
            {editable ? (
              <>
                <Edit fontSize="small" htmlColor={'#aaa'} />
                &nbsp;&nbsp;
              </>
            ) : (
              <div style={{ width: 24, minWidth: 24 }} />
            )}
            {formatLocalDate(summary.serviceDate) + ' - ' + state}
            {flags?.length && (
              <>
                &nbsp;&nbsp;&nbsp;
                <WorksheetFlags flags={flags} /> &nbsp;
              </>
            )}
            {owningProvider && ' - ' + personName(owningProvider)}
            {owner && ' - ' + personName(owner)}
            &nbsp;&nbsp;&nbsp;
          </MenuItem>
        )
      })}
    </Menu>
  )
}
